import { useCallback, useEffect, useState } from 'react';

const stores = new Map();

const readInitialDarkMode = (storageKey) => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(storageKey) === 'true';
};

const ensureStore = (storageKey) => {
  if (!stores.has(storageKey)) {
    stores.set(storageKey, {
      isDark: readInitialDarkMode(storageKey),
      subscribers: new Set(),
    });
  }
  return stores.get(storageKey);
};

const applyDarkMode = (storageKey, nextValue) => {
  const store = ensureStore(storageKey);
  store.isDark = !!nextValue;

  if (typeof window !== 'undefined') {
    const root = window.document.documentElement;
    if (store.isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    window.localStorage.setItem(storageKey, String(store.isDark));
  }

  store.subscribers.forEach((notify) => notify(store.isDark));
};

export function useDarkMode(storageKey = 'darkMode') {
  const [isDark, setIsDark] = useState(() => ensureStore(storageKey).isDark);

  useEffect(() => {
    const store = ensureStore(storageKey);
    store.subscribers.add(setIsDark);
    applyDarkMode(storageKey, store.isDark);

    const onStorage = (event) => {
      if (event.key !== storageKey) return;
      applyDarkMode(storageKey, event.newValue === 'true');
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
    }

    return () => {
      store.subscribers.delete(setIsDark);
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
      }
    };
  }, [storageKey]);

  const toggleDarkMode = useCallback(() => {
    const store = ensureStore(storageKey);
    applyDarkMode(storageKey, !store.isDark);
  }, [storageKey]);

  const setDarkMode = useCallback((value) => {
    applyDarkMode(storageKey, value);
  }, [storageKey]);

  return { isDark, toggleDarkMode, setDarkMode };
}
